import { useCallback } from 'react';
import { css } from '@emotion/react';
import { Entry, Person } from '@weave/schema-gen-ts/dist/schemas/comm/history/v1/history_service.pb';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/comm/shared/v1/enums.pb';
import { CallStatus } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { OfficeUser } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import dayjs from 'dayjs';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { PersonHelpers } from '@frontend/api-person';
import { getWeaveToken } from '@frontend/auth-helpers';
import { callStatusUtils, generateTrackingId } from '@frontend/call-intel-core';
import { IntelCard } from '@frontend/call-intel-insights';
import { ActionsUI } from '@frontend/contact-actions';
import appConfig from '@frontend/env';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon, IconColorType } from '@frontend/icons';
import { CachedAudioScrubber } from '@frontend/phone-panels';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { ListRow, styles as designSystemStyles, Text, useTooltip } from '@frontend/design-system';

type PhoneHistoryEntryProps = Pick<
  Entry,
  'direction' | 'recordDetails' | 'timestamp' | 'locationUser' | 'callIntelInsights'
> & {
  selectedEntryId?: string;
  recordId?: string;
  hasCallIntelPremiumFeatures?: boolean;
  onClick?: (args: CallIntelTypes.CallRecord) => void;
};
type SmsHistoryEntryProps = Pick<Entry, 'person' | 'personPhone' | 'recordDetails' | 'timestamp' | 'locationPhone'>;
type UnifiedDirection = 'DIRECTION_INBOUND' | 'DIRECTION_OUTBOUND';

type HistoryKindMap = {
  sms: Entry;
  phone: Entry;
  callIntel: CallIntelTypes.CallRecord;
};

type StatusBadgeProps = {
  call?: CallIntelTypes.CallRecord;
};

type HistoryEntryProps<K extends keyof HistoryKindMap> = {
  data: HistoryKindMap[K];
  kind: K;
  selectedEntryId?: string;
  recordId?: string;
  hasCallIntelPremiumFeatures?: boolean;
  onClick?: (arg: CallIntelTypes.CallRecord) => void;
};

type CallHistoryEntryProps = {
  call: CallIntelTypes.CallRecord;
  selectedEntryId?: string;
  onClick?: (args: CallIntelTypes.CallRecord) => void;
};

type PhoneIconProps = {
  direction?: UnifiedDirection;
  isAnswered?: boolean;
};

type PhoneTextProps = {
  direction?: UnifiedDirection;
  /** truthy = answered, falsy = not-answered/missed */
  isAnswered?: boolean;
  user?: OfficeUser;
};

const HISTORY_DATE_FORMAT = 'MMM D, YYYY';

const formatHistoryDate = (timestamp?: string): string => {
  if (!timestamp) return '';
  const d = dayjs(timestamp);
  return d.isValid() ? d.format(HISTORY_DATE_FORMAT) : '';
};

const isInCurrentMonth = (timestamp?: string) =>
  !!timestamp && dayjs(timestamp).isValid() && dayjs(timestamp).isSame(dayjs(), 'month');

const getPersonName = (person?: Person) => {
  if (person?.firstName) {
    return PersonHelpers.getFullName({
      FirstName: person?.firstName,
      LastName: person?.lastName,
      PreferredName: person?.preferredName,
    });
  } else {
    return i18next.t('Unknown employee', { ns: 'contacts' });
  }
};

const PhoneIcon = ({ direction, isAnswered = true }: PhoneIconProps) => {
  if (!direction) return null;

  let name: 'phone-missed' | 'phone-incoming' | 'phone-outgoing';
  let color: IconColorType | undefined;

  const inbound = direction === Direction.DIRECTION_INBOUND;
  const outbound = direction === Direction.DIRECTION_OUTBOUND;

  if (inbound && !isAnswered) {
    name = 'phone-missed';
    color = 'error' as IconColorType;
  } else if (inbound) {
    name = 'phone-incoming';
  } else if (outbound) {
    name = 'phone-outgoing';
  } else {
    return null;
  }

  return <Icon name={name} size={24} css={styles.icon} color={color} />;
};

const PhoneText = ({ direction, isAnswered = true, user }: PhoneTextProps) => {
  const { t } = useTranslation('contacts');

  if (!direction) return null;

  const inbound = direction === Direction.DIRECTION_INBOUND;
  const outbound = direction === Direction.DIRECTION_OUTBOUND;
  const person = getPersonName(user);

  let message: string;
  if (inbound && !isAnswered) {
    message = t('Missed call');
  } else if (inbound) {
    message = t('Call answered by {{person}}', { person });
  } else if (outbound) {
    message = t('Call placed by {{person}}', { person });
  } else {
    message = t('Unknown');
  }

  return (
    <Text size='medium' color='subdued'>
      {message}
    </Text>
  );
};

const StatusBadge = ({ call }: StatusBadgeProps) => {
  const { t } = useTranslation('contacts');
  const { triggerProps, tooltipProps, Tooltip } = useTooltip();
  const { isSkippedCall, isFailedCall } = callStatusUtils.getCallFlags(call);

  if (!call) return null;

  const isVoicemail = call.status === CallStatus.CALL_STATUS_VOICEMAIL;

  if (isVoicemail) {
    return (
      <span {...triggerProps}>
        <Icon name='info-filled-small' primaryColor='disabled' secondaryColor='subdued' />
        <Tooltip {...tooltipProps}>{t('Outbound voicemails are not analyzed by Call Intelligence.')}</Tooltip>
      </span>
    );
  }

  return (
    (isSkippedCall || isFailedCall) && (
      <span {...triggerProps}>
        {!isSkippedCall ? (
          <Icon name='error-badge-small' color='error' />
        ) : (
          <Icon name='help-filled-small' primaryColor='disabled' secondaryColor='subdued' />
        )}
        <Tooltip {...tooltipProps}>
          {isSkippedCall
            ? t(
                'Calls are not analyzed if they have a single participant, very short audio, or audio exceeding an hour in length.'
              )
            : t('We are unable to transcribe and analyze this call. ')}
        </Tooltip>
      </span>
    )
  );
};

const PhoneHistoryEntry = ({
  direction,
  recordDetails,
  timestamp,
  locationUser,
  callIntelInsights: call,
  recordId,
  selectedEntryId,
  hasCallIntelPremiumFeatures,
  onClick,
}: PhoneHistoryEntryProps) => {
  const answered = recordDetails?.status === 'answered';
  const isCallInCurrentMonth = isInCurrentMonth(timestamp);
  const weaveToken = getWeaveToken();

  const handleClick = useCallback(() => {
    if (call) {
      pendo.track(generateTrackingId({ component: 'contact-panel-history', context: 'call-insight-card' }));
      onClick?.(call as CallIntelTypes.CallRecord);
    }
  }, [call, onClick]);

  return (
    <ListRow
      css={styles.wrapper}
      trackingId='contact-2.0-tray-tab-history-call'
      isSelected={call?.id === (selectedEntryId ?? '')}
      onClick={call && call.status !== CallStatus.CALL_STATUS_VOICEMAIL ? handleClick : undefined}
    >
      <ListRow.Lead>
        <PhoneIcon direction={direction as UnifiedDirection} isAnswered={answered} />
      </ListRow.Lead>
      <ListRow.Content>
        <ListRow.Content.Title TrailingIcon={<StatusBadge call={call as CallIntelTypes.CallRecord} />}>
          {formatHistoryDate(timestamp)}
        </ListRow.Content.Title>
        <ListRow.Content.Subtitle>
          <PhoneText direction={direction as UnifiedDirection} isAnswered={answered} user={locationUser} />
          {call && hasCallIntelPremiumFeatures && (
            <IntelCard.Attributes isPrivate={call.isPrivate}>
              <IntelCard.Sentiment sentiment={call?.callSentiment?.sentiment} />
              <IntelCard.SchedulingOutcome call={call as CallIntelTypes.CallRecord} />
              <IntelCard.Category category={call?.categories?.[0]} />
              {isCallInCurrentMonth && <IntelCard.Tasks count={call?.taskCount} />}
            </IntelCard.Attributes>
          )}
        </ListRow.Content.Subtitle>
        {recordDetails && recordDetails.recordingPath && recordId && recordDetails.status === 'answered' && (
          <CachedAudioScrubber
            trackingId='nwx-phones-contactPanel-history-row-callRecording-audioScrubber'
            filePath={`${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${recordId}?location_id=${recordDetails.locationId}&token=${weaveToken}`}
            mediaId={recordId}
            css={{ maxWidth: '100%' }}
          />
        )}
      </ListRow.Content>
    </ListRow>
  );
};

const SmsHistoryEntry = ({ person, personPhone, recordDetails, timestamp, locationPhone }: SmsHistoryEntryProps) => {
  const { t } = useTranslation('contacts');
  const { setShow } = useSlidePanelShallowStore('setShow');
  const mediumMax = useMatchMedia({ maxWidth: breakpoints.medium.max });
  const { onClick } = ActionsUI.actions.useMessageAction();

  const handleClick = () => {
    onClick({
      personId: person?.personId,
      threadGroupId: recordDetails?.locationId,
      groupIds: recordDetails?.locationId ? [recordDetails.locationId] : undefined,
      personPhone,
      locationPhone,
      focusThreadItemData:
        recordDetails?.messageId && timestamp
          ? {
              id: recordDetails.messageId,
              createdAt: timestamp,
            }
          : undefined,
    });
    if (mediumMax) {
      setShow(false);
    }
  };

  return (
    <ListRow css={styles.wrapper} trackingId='contact-2.0-tray-tab-history-message' onClick={handleClick}>
      <ListRow.Lead>
        <Icon name='message' size={24} css={styles.icon} />
      </ListRow.Lead>
      <ListRow.Content>
        <ListRow.Content.Title>{formatHistoryDate(timestamp)}</ListRow.Content.Title>
        <ListRow.Content.Subtitle>
          <Text textAlign='left' color='light' css={designSystemStyles.multilineTruncate(2)}>
            {recordDetails?.body || t('No message content')}
          </Text>
        </ListRow.Content.Subtitle>
      </ListRow.Content>
    </ListRow>
  );
};

const CallHistoryEntry: React.FC<CallHistoryEntryProps> = ({ call, selectedEntryId, onClick }) => {
  const isCallInCurrentMonth = isInCurrentMonth(call.startTime);

  return (
    <ListRow
      css={styles.wrapper}
      onClick={() => onClick?.(call)}
      isSelected={call?.id === selectedEntryId}
      trackingId={generateTrackingId({ component: 'contact-panel-history', context: 'call-insight-card' })}
    >
      <ListRow.Lead>
        <PhoneIcon direction={call?.direction as UnifiedDirection} />
      </ListRow.Lead>
      <ListRow.Content>
        <ListRow.Content.Title TrailingIcon={<StatusBadge call={call} />}>
          {formatHistoryDate(call.startTime)}
        </ListRow.Content.Title>
        <ListRow.Content.Subtitle>
          <PhoneText direction={call.direction as UnifiedDirection} user={call.officeUser as Person} />
          <IntelCard.Attributes isPrivate={call.isPrivate}>
            <IntelCard.Sentiment sentiment={call?.callSentiment?.sentiment} />
            <IntelCard.SchedulingOutcome call={call} />
            <IntelCard.Category category={call?.categories?.[0]} />
            {isCallInCurrentMonth && <IntelCard.Tasks count={call?.taskCount} />}
          </IntelCard.Attributes>
        </ListRow.Content.Subtitle>
      </ListRow.Content>
    </ListRow>
  );
};

export const HistoryEntry = <K extends keyof HistoryKindMap>({
  kind,
  data,
  selectedEntryId,
  recordId,
  onClick,
}: HistoryEntryProps<K>) => {
  switch (kind) {
    case 'sms':
      return <SmsHistoryEntry {...(data as Entry)} />;
    case 'phone':
      return (
        <PhoneHistoryEntry
          {...(data as Entry)}
          recordId={recordId}
          onClick={onClick}
          selectedEntryId={selectedEntryId}
        />
      );
    case 'callIntel':
      return (
        <CallHistoryEntry
          call={data as CallIntelTypes.CallRecord}
          onClick={onClick}
          selectedEntryId={selectedEntryId}
        />
      );
    default:
      return null;
  }
};

const styles = {
  wrapper: css`
    background-color: ${theme.colors.white};

    .content-column-title-container {
      display: flex;
      align-items: center;
    }
  `,
  icon: css`
    min-width: ${theme.spacing(3)};
  `,
};
